/**
 * Customer Service
 *
 * Type-safe customer management service using @monorepo/shared schemas
 */

import {
  type LiveBillResponse,
  LiveBillResponseSchema,
} from '@monorepo/shared/schemas/billing'
import {
  type CreateCustomer,
  CreateCustomerSchema,
  type CreateCustomerTax,
  type CreateOrganization,
  CreateOrganizationSchema,
  type Customer,
  type CustomerQuery,
  type CustomerResponse,
  CustomerResponseSchema,
  type CustomerStatus,
  type CustomerTax,
  type CustomerType,
  type Organization,
  type OrganizationQuery,
  type OrganizationResponse,
  OrganizationResponseSchema,
  type PaginatedCustomersResponse,
  PaginatedCustomersResponseSchema,
  type PaginatedOrganizationsResponse,
  PaginatedOrganizationsResponseSchema,
  type ProfileKind,
  type UpdateCustomer,
  UpdateCustomerSchema,
  type UpdateOrganization,
  UpdateOrganizationSchema,
} from '@monorepo/shared/schemas/customers'
import {
  type AssignTaxes,
  AssignTaxesSchema,
  type CustomerTaxesResponse,
  CustomerTaxesResponseSchema,
  type RemoveTaxResponse,
  RemoveTaxResponseSchema,
} from '@monorepo/shared/schemas/taxes'
import {
  type CustomerAgentUsageResponse,
  CustomerAgentUsageResponseSchema,
  type CustomerDailyUsageResponse,
  CustomerDailyUsageResponseSchema,
} from '@monorepo/shared/schemas/usage'
import { del, get, post, put } from '@/lib/api-client'

class CustomerService {
  /**
   * Search/list customers with filters
   */
  async searchCustomers(
    query?: CustomerQuery
  ): Promise<PaginatedCustomersResponse> {
    const queryParams = new URLSearchParams()

    if (query?.organization_id)
      queryParams.append('organization_id', query.organization_id)
    if (query?.type) queryParams.append('type', query.type)
    if (query?.profile_kind)
      queryParams.append('profile_kind', query.profile_kind)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.external_id) queryParams.append('external_id', query.external_id)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    // NEW: Field-based filtering
    if (query?.filter_field)
      queryParams.append('filter_field', query.filter_field)
    if (query?.filter_operator)
      queryParams.append('filter_operator', query.filter_operator)
    if (query?.filter_value)
      queryParams.append('filter_value', query.filter_value)

    const url = `/customers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedCustomersResponseSchema)
  }

  /**
   * Get all customers (simple listing)
   */
  async getAllCustomers(
    page = 1,
    limit = 20
  ): Promise<PaginatedCustomersResponse> {
    return this.searchCustomers({ page, limit })
  }

  /**
   * List all customers (alias for searchCustomers)
   */
  async getCustomers(
    query?: CustomerQuery
  ): Promise<PaginatedCustomersResponse> {
    return this.searchCustomers(query)
  }

  /**
   * Get customer by ID
   */
  async getCustomerById(id: string): Promise<CustomerResponse> {
    return get(`/customers/${id}`, CustomerResponseSchema)
  }

  /**
   * Get customer by ID (alias for getCustomerById)
   */
  async getCustomer(id: string): Promise<CustomerResponse> {
    return this.getCustomerById(id)
  }

  /**
   * Create new customer
   */
  async createCustomer(data: CreateCustomer): Promise<CustomerResponse> {
    const validated = CreateCustomerSchema.parse(data)

    return post('/customers', validated, CustomerResponseSchema)
  }

  /**
   * Update customer
   */
  async updateCustomer(
    id: string,
    data: UpdateCustomer
  ): Promise<CustomerResponse> {
    const validated = UpdateCustomerSchema.parse(data)

    return put(`/customers/${id}`, validated, CustomerResponseSchema)
  }

  /**
   * Delete customer
   */
  async deleteCustomer(id: string): Promise<void> {
    await del(`/customers/${id}`)
  }

  /**
   * Update customer status
   */
  async updateStatus(
    id: string,
    status: CustomerStatus
  ): Promise<CustomerResponse> {
    const validated = UpdateCustomerSchema.parse({ status })

    return put(`/customers/${id}`, validated, CustomerResponseSchema)
  }

  // ============================================================================
  // ORGANIZATION METHODS
  // ============================================================================

  /**
   * List all organizations
   */
  async getAllOrganizations(
    query?: OrganizationQuery
  ): Promise<PaginatedOrganizationsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.external_id) queryParams.append('external_id', query.external_id)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/organizations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedOrganizationsResponseSchema)
  }

  /**
   * Get organization by ID
   */
  async getOrganizationById(id: string): Promise<OrganizationResponse> {
    return get(`/organizations/${id}`, OrganizationResponseSchema)
  }

  /**
   * Create new organization
   */
  async createOrganization(
    data: CreateOrganization
  ): Promise<OrganizationResponse> {
    const validated = CreateOrganizationSchema.parse(data)

    return post('/organizations', validated, OrganizationResponseSchema)
  }

  /**
   * Update organization
   */
  async updateOrganization(
    id: string,
    data: UpdateOrganization
  ): Promise<OrganizationResponse> {
    const validated = UpdateOrganizationSchema.parse(data)

    return put(`/organizations/${id}`, validated, OrganizationResponseSchema)
  }

  /**
   * Delete organization
   */
  async deleteOrganization(id: string): Promise<void> {
    await del(`/organizations/${id}`)
  }

  /**
   * Assign customer to organization
   */
  async assignCustomerToOrganization(
    customerId: string,
    organizationId: string
  ): Promise<CustomerResponse> {
    return this.updateCustomer(customerId, { organization_id: organizationId })
  }

  /**
   * Get customers by organization
   */
  async getCustomersByOrganization(
    organizationId: string
  ): Promise<PaginatedCustomersResponse> {
    return this.searchCustomers({
      organization_id: organizationId,
      limit: 1000,
    })
  }

  // ============================================================================
  // TAX METHODS
  // ============================================================================

  /**
   * Assign taxes to customer (customer-specific, overrides organization defaults)
   */
  async assignTaxes(
    customerId: string,
    data: AssignTaxes
  ): Promise<CustomerTaxesResponse> {
    const validated = AssignTaxesSchema.parse(data)
    return post(
      `/customers/${customerId}/taxes`,
      validated,
      CustomerTaxesResponseSchema
    )
  }

  /**
   * Get customer taxes (includes customer-specific, organization, and effective taxes)
   */
  async getCustomerTaxes(customerId: string): Promise<CustomerTaxesResponse> {
    return get(`/customers/${customerId}/taxes`, CustomerTaxesResponseSchema)
  }

  /**
   * Remove tax from customer
   */
  async removeTax(
    customerId: string,
    taxId: string
  ): Promise<RemoveTaxResponse> {
    return del(
      `/customers/${customerId}/taxes/${taxId}`,
      RemoveTaxResponseSchema
    )
  }

  // ============================================================================
  // USAGE & BILLING METHODS
  // ============================================================================

  /**
   * Get per-agent signal usage breakdown for a customer
   */
  async getCustomerAgentUsage(
    customerId: string,
    fromDate?: string,
    toDate?: string,
    agentId?: string
  ): Promise<CustomerAgentUsageResponse> {
    const params = new URLSearchParams()
    if (fromDate) params.append('from_date', fromDate)
    if (toDate) params.append('to_date', toDate)
    if (agentId) params.append('agent_id', agentId)
    const qs = params.toString()
    return get(
      `/customers/${customerId}/usage${qs ? `?${qs}` : ''}`,
      CustomerAgentUsageResponseSchema
    )
  }

  /**
   * Get per-day usage trend for a customer
   */
  async getCustomerDailyUsage(
    customerId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<CustomerDailyUsageResponse> {
    const params = new URLSearchParams()
    if (fromDate) params.append('from_date', fromDate)
    if (toDate) params.append('to_date', toDate)
    if (!fromDate && !toDate) params.append('days', '30')
    return get(
      `/customers/${customerId}/usage/daily?${params.toString()}`,
      CustomerDailyUsageResponseSchema
    )
  }

  /**
   * Get live bill (contract-based revenue) for a customer
   */
  async getLiveBill(
    customerId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<LiveBillResponse> {
    const params = new URLSearchParams({ customer_id: customerId })
    if (fromDate) params.set('from_date', fromDate)
    if (toDate) params.set('to_date', toDate)
    return get(
      `/current-bill/live?${params.toString()}`,
      LiveBillResponseSchema
    )
  }
}

export const customerService = new CustomerService()

// Re-export types for convenience
export type {
  Customer,
  CustomerResponse,
  CreateCustomer,
  UpdateCustomer,
  PaginatedCustomersResponse,
  CustomerQuery,
  Organization,
  OrganizationResponse,
  CreateOrganization,
  UpdateOrganization,
  PaginatedOrganizationsResponse,
  OrganizationQuery,
  CustomerTax,
  CreateCustomerTax,
  CustomerType,
  CustomerStatus,
  ProfileKind,
  AssignTaxes,
  CustomerTaxesResponse,
  RemoveTaxResponse,
  CustomerAgentUsageResponse,
  CustomerDailyUsageResponse,
  LiveBillResponse,
}
